import { useCallback } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import { useAuthStore } from '@/store/auth'
import { redirectToLoginOnSessionExpired } from '@/utils/session-error'

import {
  emailVerificationNotificationMutation,
  forgotPasswordMutation,
  loginMutation,
  logoutMutation,
  refreshTokenMutation,
  registerMutation,
  resetPasswordMutation
} from './mutations'
import { ForgotPasswordRequest, LoginRequest, RegisterRequest, ResetPasswordRequest } from './types'
import { USER } from '@/api/endpoints'
import { User } from '@/api/users/types'

let refreshPromise: Promise<boolean> | null = null

export const useAuth = () => {
  const { isAuthenticated, setToken, clearToken, getAccessToken } = useAuthStore()
  const {
    data: user,
    error: userError,
    isLoading: isLoadingUser,
    mutate: mutateUser
  } = useSWR<User>(isAuthenticated ? USER.PROFILE : null)

  /**
   * register
   */
  const {
    trigger: register,
    isMutating: isRegistering,
    error: registerError
  } = useSWRMutation('auth/register', registerMutation)

  /**
   * forgot password
   */
  const {
    trigger: forgotPassword,
    isMutating: isForgotPasswordLoading,
    error: forgotPasswordError
  } = useSWRMutation('auth/forgot-password', forgotPasswordMutation)

  /**
   * reset password
   */
  const {
    trigger: resetPassword,
    isMutating: isResetPasswordLoading,
    error: resetPasswordError
  } = useSWRMutation('auth/reset-password', resetPasswordMutation)

  /**
   * email verification notification
   */
  const {
    trigger: sendEmailVerification,
    isMutating: isSendingEmailVerification,
    error: emailVerificationError
  } = useSWRMutation('auth/email-verification', emailVerificationNotificationMutation)

  /**
   * login
   */
  const {
    trigger: login,
    isMutating: isLoggingIn,
    error: loginError
  } = useSWRMutation('auth/login', loginMutation, {
    onSuccess: ({ token }) => {
      setToken(token)
      mutateUser()
    }
  })

  /**
   * logout
   */
  const { trigger: logout, isMutating: isLoggingOut } = useSWRMutation(
    'auth/logout',
    logoutMutation,
    {
      onSuccess: () => {
        clearToken()
        mutateUser(undefined, false)
      },
      onError: () => {
        clearToken()
        mutateUser(undefined, false)
      }
    }
  )

  /**
   * refresh token
   */
  const { trigger: refreshToken } = useSWRMutation('auth/refresh', refreshTokenMutation, {
    onSuccess: ({ token }) => {
      setToken(token)
      mutateUser()
    },
    onError: () => {
      clearToken()
      mutateUser(undefined, false)
      redirectToLoginOnSessionExpired()
    }
  })

  const refreshTokenWithLock = useCallback(() => {
    const token = getAccessToken()
    if (!token) {
      clearToken()
      redirectToLoginOnSessionExpired()
      return Promise.resolve(false)
    }

    if (refreshPromise) {
      return refreshPromise
    }

    refreshPromise = refreshToken({ token })
      .then(() => true)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null
      })

    return refreshPromise
  }, [clearToken, getAccessToken, refreshToken])

  return {
    // state
    user,
    isAuthenticated,
    isLoadingUser,
    // actions
    login: (credentials: LoginRequest) => login(credentials),
    register: (data: RegisterRequest) => register(data),
    forgotPassword: (data: ForgotPasswordRequest) => forgotPassword(data),
    resetPassword: (data: ResetPasswordRequest) => resetPassword(data),
    sendEmailVerification: () => {
      const token = getAccessToken()
      if (token) {
        return sendEmailVerification({ token })
      }
      return Promise.reject(new Error('No token available'))
    },
    logout: () => {
      const token = getAccessToken()
      if (token) {
        return logout({ token })
      }
      clearToken()
    },
    refreshToken: refreshTokenWithLock,
    refreshTokenWithLock,
    mutateUser,
    // loading states
    isLoggingIn,
    isLoggingOut,
    isRegistering,
    isForgotPasswordLoading,
    isResetPasswordLoading,
    isSendingEmailVerification,
    // errors
    loginError,
    registerError,
    forgotPasswordError,
    resetPasswordError,
    emailVerificationError,
    userError
  }
}
